import React, { useCallback, useEffect, useState } from 'react'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { ViewTabs } from '@/components/ui/tabs'
import { SortDescriptor } from '@progress/kendo-data-query'
import { GridSortChangeEvent } from '@progress/kendo-react-grid'
import { Button, Dropdown, Menu } from 'antd'
import {
  AppropriatenessTestDto,
  AppropriatenessTestFilterKey,
  TestTypes,
} from 'api/Clients/Types/AppropriatenessTestDto'
import { TickmillCompany } from 'api/Clients/Types/TickmillCompany'
import { TickmillCompanyTypes } from 'api/Clients/Types/TickmillCompanyTypes'
import { PageData } from 'api/Pagination/Types'
import { FlexBox } from 'components/Box'
import { Filter } from 'components/Filter'
import { getAppropriatenessTestFields } from 'components/Filter/ComplianceFields/getAppropriatenessTestFields'
import { DownOutlined, PlusCircleOutlined } from 'components/Icon'
import { InfoScreen } from 'components/InfoScreen'
import { Loading } from 'components/Loading'
import { createGridState, normalizeFilter } from 'features/DataGrid/helpers'
import { useGridFilter, useGridState, useGridStatusState } from 'features/DataGrid/hooks'
import { isGoBack } from 'lib/react-router-dom'
import { useAppDispatch, useAppSelector } from 'store'
import {
  fetchClientAppropriatenessTestCategoriesAction,
  fetchClientAppropriatenessTestSubCategoriesAction,
} from 'store/clients/clients.actions'
import {
  CompositeFilterDescriptor,
  FilterChangeEvent,
  FilterDescriptor,
  GridFilterActionState,
} from 'store/grid'
import { fetchTickmillCompaniesAction } from 'store/identity/identity.actions'
import { GridFilterOperatorEnum } from 'types/GridFilterOperatorEnum'
import { PageQuery } from 'utils/api'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientsApiClient } from 'utils/api/clientsApi'
import { IdentityApiClient } from 'utils/api/identityApi'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import { buildQuery, buildSortQuery } from 'utils/QueryHelpers'

import { TableStateContextData } from '../../../utils/TableStateContext'
import AppropriatnessTestBuilderForm from './AppropriatnessTestBuilderForm'
import TestsTable from './TestsTable'

const useMapStateToProps = () => ({
  appropriatenessTestCategories: useAppSelector(
    (state) => state.clients.appropriatenessTestCategories
  ),
  appropriatenessTestSubCategories: useAppSelector(
    (state) => state.clients.appropriatenessTestSubCategories
  ),
  tickmillCompanies: useAppSelector((state) => state.identity.tickmillCompanies),
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()

  const identityApiClient = useApiClient(IdentityApiClient)
  const clientsApiClient = useApiClient(ClientsApiClient)

  return {
    fetchClientAppropriatenessTestCategories: async () =>
      dispatch(fetchClientAppropriatenessTestCategoriesAction(clientsApiClient)),
    fetchClientAppropriatenessTestSubCategories: async () =>
      dispatch(fetchClientAppropriatenessTestSubCategoriesAction(clientsApiClient)),
    fetchTickmillCompanies: async () => dispatch(fetchTickmillCompaniesAction(identityApiClient)),
  }
}

enum TestStatusTabs {
  ACTIVE_TEST_SETS = 'Active Test Sets',
  TEST_SETS = 'Test Sets',
  DRAFT = 'Draft',
  NEW_APPROPRIATENESS_TEST = 'New Appropriateness Test',
}

const APPROPRIATENESS_TEST_VERSION_STORAGE_KEY = 'appropriateness_test_versions'
const ECONOMIC_PROFILE_VERSION_STORAGE_KEY = 'economic_profile_versions'

const getStoredVersions = (): number[] => {
  try {
    const stored = localStorage.getItem(APPROPRIATENESS_TEST_VERSION_STORAGE_KEY)
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

const getStoredEconomicProfileVersions = (): number[] => {
  try {
    const stored = localStorage.getItem(ECONOMIC_PROFILE_VERSION_STORAGE_KEY)
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

const saveVersionsToStorage = (versions: number[]): void => {
  try {
    localStorage.setItem(APPROPRIATENESS_TEST_VERSION_STORAGE_KEY, JSON.stringify(versions))
  } catch (error) {
    console.error('Failed to save versions to localStorage:', error)
  }
}

const saveEconomicProfileVersionsToStorage = (versions: number[]): void => {
  try {
    localStorage.setItem(ECONOMIC_PROFILE_VERSION_STORAGE_KEY, JSON.stringify(versions))
  } catch (error) {
    console.error('Failed to save Economic Profile versions to localStorage:', error)
  }
}

const getNextVersionNumber = (existingVersions: number[]): number => {
  if (existingVersions.length === 0) return 1
  return Math.max(...existingVersions) + 1
}

const AppropriatenessTestPage = (): JSX.Element => {
  const crumbs = [{ title: 'Appropriateness Test', link: links.complianceAppropriatenessTests.url }]
  const clientsApiClient = useApiClient(ClientsApiClient)
  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })
  const [tabs, setTabs] = useState<string>(TestStatusTabs.ACTIVE_TEST_SETS)
  const [tests, setTests] = useState<AppropriatenessTestDto[]>([])
  const [selectedCompany, setSelectedCompany] = useState<TickmillCompany>({
    id: TickmillCompanyTypes.Tickmill_EU_Company,
    name: 'Tickmill EU Company',
  })
  const [selectedTestType, setSelectedTestType] = useState<TestTypes>(TestTypes.ALL)

  const [versions, setVersions] = useState<number[]>(getStoredVersions())
  const [activeVersions, setActiveVersions] = useState<Set<number>>(new Set())
  const [economicProfileVersions, setEconomicProfileVersions] = useState<number[]>(
    getStoredEconomicProfileVersions()
  )
  const [activeEconomicProfileVersions, setActiveEconomicProfileVersions] = useState<Set<number>>(
    new Set()
  )
  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const { gridState, gridActions } = useGridState()
  const { selectFilterForGoBackHistory } = useGridFilter(gridStatusActions)

  const { appropriatenessTestCategories, appropriatenessTestSubCategories, tickmillCompanies } =
    useMapStateToProps()
  const {
    fetchClientAppropriatenessTestCategories,
    fetchClientAppropriatenessTestSubCategories,
    fetchTickmillCompanies,
  } = useMapDispatchToProps()

  const handleCompanyChange = async (company: TickmillCompany) => {
    setSelectedCompany(company)
    await fetchGrid(
      async (
        gridFilterState: GridFilterActionState
      ): Promise<PageData<AppropriatenessTestDto[]>> => {
        const searchQuery = getQuery(gridFilterState.filter, tabs, selectedTestType, company.id)
        const sortQuery = getSort(gridFilterState.sort)
        const response = await clientsApiClient.getAppropriatenessTests({
          search: searchQuery,
          sort: sortQuery,
          pageIndex: gridFilterState.pageIndex,
          pageSize: gridFilterState.take,
          skipCount: tableData?.skipCount !== false,
        })

        if (response?.items) {
          setTests(response.items)
        }
        setTableData({ ...tableData, meta: response })
        return response
      }
    )
  }

  useEffect(() => {
    ;(async () => {
      await fetchGrid(
        async (
          gridFilterState: GridFilterActionState
        ): Promise<PageData<AppropriatenessTestDto[]>> => {
          const searchQuery = getQuery(
            gridFilterState.filter,
            tabs,
            selectedTestType,
            selectedCompany.id
          )
          const sortQuery = getSort(gridFilterState.sort)
          const response = await clientsApiClient.getAppropriatenessTests({
            search: searchQuery,
            sort: sortQuery,
            pageIndex: gridFilterState.pageIndex,
            pageSize: gridFilterState.take,
            skipCount: tableData?.skipCount !== false,
          })

          if (isEmpty(appropriatenessTestCategories)) {
            await fetchClientAppropriatenessTestCategories()
          }
          if (isEmpty(appropriatenessTestSubCategories)) {
            await fetchClientAppropriatenessTestSubCategories()
          }
          if (isEmpty(tickmillCompanies)) {
            await fetchTickmillCompanies()
          }
          if (response?.items) {
            setTests(response.items)
          }
          setTableData({ ...tableData, meta: response })
          return response
        }
      )
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGrid = async (
    callback: (
      gridFilterState: GridFilterActionState
    ) => Promise<PageData<AppropriatenessTestDto[]>>
  ) => {
    gridStatusActions.setLoading(true)

    const gridFilterState = selectFilterForGoBackHistory(
      createGridState(getPaginationInitialState(), gridState, { isGoBack })
    )

    const response = await callback(gridFilterState)
    if (response?.totalResults >= 0) {
      const total = response.totalResults

      gridActions.setGridFilters({
        total,
      })
    }

    gridStatusActions.setLoading(false)
  }

  const getSort = useCallback(
    (sortDescriptors?: SortDescriptor[]): string | null => {
      return buildSortQuery(sortDescriptors ?? gridState.sort)
    },
    [gridState.sort]
  )

  const getStatusIdForTab = (tab: string): number | null => {
    switch (tab) {
      case TestStatusTabs.ACTIVE_TEST_SETS:
        return 2
      case TestStatusTabs.DRAFT:
        return 1
      case TestStatusTabs.TEST_SETS:
        return 3
      default:
        return null
    }
  }

  const createDefaultFilters = (
    statusId?: number | null,
    testType?: TestTypes,
    companyId?: number
  ): CompositeFilterDescriptor => {
    const baseFilters: FilterDescriptor[] = [
      {
        field: AppropriatenessTestFilterKey.tickmillCompany,
        operator: GridFilterOperatorEnum.equalOperator,
        value: companyId ?? selectedCompany.id,
      },
      {
        field: AppropriatenessTestFilterKey.status,
        operator: GridFilterOperatorEnum.equalOperator,
        value: statusId || 2,
      },
    ]

    if (statusId === 3 && testType) {
      if (testType === TestTypes.ALL) {
        const testCategoryFilters: CompositeFilterDescriptor = {
          logic: 'or',
          filters: [
            {
              field: AppropriatenessTestFilterKey.category,
              operator: GridFilterOperatorEnum.equalOperator,
              value: 2,
            },
            {
              field: AppropriatenessTestFilterKey.category,
              operator: GridFilterOperatorEnum.equalOperator,
              value: 9,
            },
          ],
        }
        return {
          logic: 'and',
          filters: [...baseFilters, testCategoryFilters],
        }
      } else {
        const testCategoryId = testType === TestTypes.APPROPRIATENESS_TEST ? 2 : 9
        baseFilters.push({
          field: AppropriatenessTestFilterKey.category,
          operator: GridFilterOperatorEnum.equalOperator,
          value: testCategoryId,
        })
      }
    }

    return {
      logic: 'and',
      filters: baseFilters,
    }
  }

  const getQuery = (
    filter?: CompositeFilterDescriptor,
    currentTabs?: string,
    currentTestType?: TestTypes,
    currentCompanyId?: number
  ) => {
    const gridFilter = filter ?? gridState.filter
    const statusId = getStatusIdForTab(currentTabs ?? tabs)
    const testType = currentTestType ?? selectedTestType
    const companyId = currentCompanyId ?? selectedCompany.id

    return (
      buildQuery({
        ...gridFilter,
        logic: gridFilter.logic,
        defaultFilters: createDefaultFilters(statusId, testType, companyId),
      }) ?? null
    )
  }

  const getFilteredTests = (): AppropriatenessTestDto[] => {
    switch (tabs) {
      case TestStatusTabs.ACTIVE_TEST_SETS:
        return tests.filter((test) => test.status.id === 2)
      case TestStatusTabs.DRAFT:
        return tests.filter((test) => test.status.id === 1)
      case TestStatusTabs.TEST_SETS:
        return tests.filter((test) => test.status.id === 3)
      default:
        return tests
    }
  }

  const filterFetch = useCallback(
    async (query?: PageQuery): Promise<void> => {
      const existingSearchQuery = query?.search ?? getQuery()
      const searchString = existingSearchQuery
      const sortQuery = query?.sort ?? getSort(gridState.sort)
      const pageIndex = query?.pageIndex ?? gridState.pageIndex ?? 1
      const pageSize = query?.pageSize ?? gridState.pageSize ?? 10
      const take = pageSize
      const skip = (pageIndex - 1) * pageSize

      const response = await clientsApiClient.getAppropriatenessTests({
        search: searchString,
        sort: sortQuery,
        pageIndex,
        pageSize,
        skipCount: tableData?.skipCount !== false,
      })
      const total = response.totalResults

      if (response?.items) {
        setTests(response.items)
      }
      gridActions.setGridFilters({
        take,
        skip,
        total,
        pageIndex,
      })
      setTableData({ ...tableData, meta: response })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      getQuery,
      getSort,
      gridActions,
      gridState.pageIndex,
      gridState.pageSize,
      gridState.skip,
      gridState.sort,
      gridState.take,
      tableData?.skipCount,
    ]
  )

  const handleSkipCountChange = async (skipCount: boolean) => {
    const existingSearchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)

    const response = await clientsApiClient.getAppropriatenessTests({
      search: existingSearchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.take,
      skipCount,
    })
    const total = response.totalResults

    if (response?.items) {
      setTests(response.items)
    }
    gridActions.setGridFilters({
      total,
    })
    setTableData({ ...tableData, skipCount, meta: response })
  }

  const handleSortChange = async (event: GridSortChangeEvent) => {
    gridActions.setGridFilters({
      sort: event.sort,
    })

    const searchQuery = getQuery(gridState.filter)
    const sortQuery = getSort(event.sort)
    const response = await clientsApiClient.getAppropriatenessTests({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.pageSize,
      skipCount: tableData?.skipCount !== false,
    })

    if (response?.items) {
      setTests(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handlePageSizeChange = async (pageSize: number) => {
    await gridActions.setGridFilters({
      ...gridState,
      pageSize,
    })

    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)

    const response = await clientsApiClient.getAppropriatenessTests({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: 1,
      pageSize,
      skipCount: tableData?.skipCount !== false,
    })
    await gridActions.setGridFilters({
      ...gridState,
      pageIndex: 1,
      take: pageSize,
      total: response.totalResults,
    })
    if (response?.items) {
      setTests(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleGridPageChange = async (page: number) => {
    const searchQuery = getQuery()
    const sortQuery = getSort(gridState.sort)
    const pageIndex = page
    gridActions.setGridFilters({
      pageIndex,
    })

    const response = await clientsApiClient.getAppropriatenessTests({
      search: searchQuery,
      sort: sortQuery,
      pageIndex,
      pageSize: gridState.take,
      skipCount: tableData?.skipCount !== false,
    })
    const total = response.totalResults

    gridActions.setGridFilters({
      total,
    })

    if (response?.items) {
      setTests(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleFilterFetch = async () => {
    await filterFetch({ pageIndex: 1 })
  }

  const handleFilterChange = (event: FilterChangeEvent) => {
    gridStatusActions.setCreateMode(true)
    gridStatusActions.setFilterMode(true)
    gridActions.setGridFilters({
      filter: normalizeFilter(event.filter),
    })
  }

  {
    /*  
    
    //We might need to restore in future if required

    const handleCreateFilter = () => {
    gridStatusActions.setCreateMode(true)
    gridStatusActions.setFilterMode(true)
    gridStatusActions.setFilterSelected(false)
  }

  const handleResetFilter = async () => {
    await resetAll()
    await filterFetch({ query: null })
  }

  const resetAll = async () => {
    await gridStatusActions.resetAll()
  }
    */
  }

  const handleOpenNewAppropriatenessTestTab = () => {
    const nextVersion = getNextVersionNumber(versions)
    const newVersions = [...versions, nextVersion]
    setVersions(newVersions)

    const newActiveVersions = new Set(activeVersions)
    newActiveVersions.add(nextVersion)
    setActiveVersions(newActiveVersions)

    saveVersionsToStorage(newVersions)
    setTabs(`VERSION_${nextVersion}`)
  }

  const handleCloseVersionTab = (version: number) => {
    const newActiveVersions = new Set(activeVersions)
    newActiveVersions.delete(version)
    setActiveVersions(newActiveVersions)

    if (tabs === `VERSION_${version}`) {
      setTabs(TestStatusTabs.ACTIVE_TEST_SETS)
    }
  }

  const handleOpenNewEconomicProfileTab = () => {
    const nextVersion = getNextVersionNumber(economicProfileVersions)
    const newVersions = [...economicProfileVersions, nextVersion]
    setEconomicProfileVersions(newVersions)

    const newActiveVersions = new Set(activeEconomicProfileVersions)
    newActiveVersions.add(nextVersion)
    setActiveEconomicProfileVersions(newActiveVersions)

    saveEconomicProfileVersionsToStorage(newVersions)
    setTabs(`ECONOMIC_PROFILE_VERSION_${nextVersion}`)
  }

  const handleCloseEconomicProfileVersionTab = (version: number) => {
    const newActiveVersions = new Set(activeEconomicProfileVersions)
    newActiveVersions.delete(version)
    setActiveEconomicProfileVersions(newActiveVersions)

    if (tabs === `ECONOMIC_PROFILE_VERSION_${version}`) {
      setTabs(TestStatusTabs.ACTIVE_TEST_SETS)
    }
  }

  const handleTabChange = async (newTab: string) => {
    setTabs(newTab)
    gridStatusActions.setLoading(true)

    try {
      const searchQuery = getQuery(undefined, newTab, selectedTestType, selectedCompany.id)
      const sortQuery = getSort(gridState.sort)

      const response = await clientsApiClient.getAppropriatenessTests({
        search: searchQuery,
        sort: sortQuery,
        pageIndex: 1,
        pageSize: gridState.pageSize ?? 10,
        skipCount: tableData?.skipCount !== false,
      })

      if (response?.items) {
        setTests(response.items)
      }
      gridActions.setGridFilters({
        pageIndex: 1,
        total: response.totalResults,
      })
      setTableData({ ...tableData, meta: response })
    } finally {
      gridStatusActions.setLoading(false)
    }
  }

  const handleTestTypeChange = async (testType: TestTypes) => {
    setSelectedTestType(testType)
    const searchQuery = getQuery(undefined, tabs, testType, selectedCompany.id)
    const sortQuery = getSort(gridState.sort)

    const response = await clientsApiClient.getAppropriatenessTests({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: 1,
      pageSize: gridState.pageSize ?? 10,
      skipCount: tableData?.skipCount !== false,
    })

    if (response?.items) {
      setTests(response.items)
    }
    gridActions.setGridFilters({
      pageIndex: 1,
      total: response.totalResults,
    })
    setTableData({ ...tableData, meta: response })
  }

  const versionTabs = Array.from(activeVersions).map((version) => ({
    key: `VERSION_${version}`,
    translateKey: <span>AP V{version}</span>,
    visible: true,
    component: (
      <AppropriatnessTestBuilderForm
        version={version}
        testType='appropriateness'
        onClose={() => handleCloseVersionTab(version)}
      />
    ),
  }))

  const economicProfileVersionTabs = Array.from(activeEconomicProfileVersions).map((version) => ({
    key: `ECONOMIC_PROFILE_VERSION_${version}`,
    translateKey: <span>Economic Profile V{version}</span>,
    visible: true,
    component: (
      <AppropriatnessTestBuilderForm
        version={version}
        testType='economic-profile'
        onClose={() => handleCloseEconomicProfileVersionTab(version)}
      />
    ),
  }))

  return (
    <React.Fragment>
      <Breadcrumbs crumbs={crumbs} />
      {/* 
      
      We might need to restore in future if required
     
     <FlexBox alignItems='center' justifyContent='flex-start'>
        <FlexBox>
          <Dropdown
            overlay={
              <Menu>
                <Menu.Item
                  onClick={handleCreateFilter}
                  disabled={gridStatusState.isFilterMode}
                  data-test='create-filter-button'
                >
                  Create filter
                </Menu.Item>
                <Menu.Item onClick={handleResetFilter} data-test='clear-filter-button'>
                  Clear
                </Menu.Item>
              </Menu>
            }
            data-test='more-filter-dropdown-block'
          >
            <Button data-test='more-button'>
              More <DownOutlined />
            </Button>
          </Dropdown>
        </FlexBox>
        <Button onClick={handleFilterFetch} data-test='filter-search-button'>
          <SearchOutlined /> Filter Search
        </Button>
      </FlexBox> */}
      {gridStatusState.isFilterMode && (
        <Filter
          value={gridState.filter}
          fields={[...getAppropriatenessTestFields()]}
          onChange={handleFilterChange}
          onKeyDown={handleFilterFetch}
        />
      )}
      <FlexBox
        style={{ marginTop: 16, marginBottom: 36 }}
        alignItems='center'
        justifyContent='space-between'
      >
        <FlexBox style={{ gap: 10 }} alignItems='center'>
          <Dropdown
            overlay={
              <Menu>
                {tickmillCompanies && tickmillCompanies.length > 0 ? (
                  tickmillCompanies.map((company: TickmillCompany) => (
                    <Menu.Item
                      key={company.id}
                      onClick={() => handleCompanyChange(company)}
                      disabled={selectedCompany.id === company.id}
                    >
                      {company.name}
                    </Menu.Item>
                  ))
                ) : (
                  <Menu.Item disabled>No companies available</Menu.Item>
                )}
              </Menu>
            }
          >
            <Button>
              {selectedCompany ? selectedCompany.name : 'Select Company'} <DownOutlined />
            </Button>
          </Dropdown>
          {tabs === TestStatusTabs.TEST_SETS && (
            <Dropdown
              overlay={
                <Menu>
                  {Object.values(TestTypes).map((testType) => (
                    <Menu.Item
                      key={testType}
                      onClick={() => handleTestTypeChange(testType)}
                      disabled={selectedTestType === testType}
                    >
                      {testType}
                    </Menu.Item>
                  ))}
                </Menu>
              }
            >
              <Button>
                {selectedTestType} <DownOutlined />
              </Button>
            </Dropdown>
          )}
        </FlexBox>
        <FlexBox style={{ gap: 10 }}>
          <Button data-test='appropriatness-test' onClick={handleOpenNewAppropriatenessTestTab}>
            <PlusCircleOutlined /> Appropriatness test
          </Button>
          <Button data-test='economic-profile' onClick={handleOpenNewEconomicProfileTab}>
            <PlusCircleOutlined />
            Economic Profile
          </Button>
        </FlexBox>
      </FlexBox>
      <ViewTabs
        defaultKey={TestStatusTabs.ACTIVE_TEST_SETS}
        activeTab={tabs}
        onChange={handleTabChange}
        tabs={[
          {
            key: TestStatusTabs.ACTIVE_TEST_SETS,
            translateKey: <span>Active Test Sets</span>,
            visible: true,
            component: (
              <TestsTable
                tests={getFilteredTests()}
                gridState={gridState}
                gridStatusState={gridStatusState}
                tableData={tableData}
                setTableData={setTableData}
                handleSortChange={handleSortChange}
                handleSkipCountChange={handleSkipCountChange}
                handleGridPageChange={handleGridPageChange}
                handlePageSizeChange={handlePageSizeChange}
              />
            ),
          },
          {
            key: TestStatusTabs.TEST_SETS,
            translateKey: <span>Test Sets</span>,
            visible: true,
            component: (
              <TestsTable
                tests={getFilteredTests()}
                gridState={gridState}
                gridStatusState={gridStatusState}
                tableData={tableData}
                setTableData={setTableData}
                handleSortChange={handleSortChange}
                handleSkipCountChange={handleSkipCountChange}
                handleGridPageChange={handleGridPageChange}
                handlePageSizeChange={handlePageSizeChange}
                hideScoreSettings={true}
              />
            ),
          },
          {
            key: TestStatusTabs.DRAFT,
            translateKey: <span>Draft</span>,
            visible: true,
            component: (() => {
              const draftTests = getFilteredTests()
              return (
                <Loading isLoading={gridStatusState.isLoading}>
                  {draftTests.length === 0 ? (
                    <InfoScreen
                      title='There are no saved draft tests yet.'
                      description='Drafts will appear here only after you click Save Draft while creating or editing a test.'
                      instructions='To get started, choose between Appropriateness Test or Economic Profile Test and create your first test.'
                    />
                  ) : (
                    <TestsTable
                      tests={draftTests}
                      gridState={gridState}
                      gridStatusState={gridStatusState}
                      tableData={tableData}
                      setTableData={setTableData}
                      handleSortChange={handleSortChange}
                      handleSkipCountChange={handleSkipCountChange}
                      handleGridPageChange={handleGridPageChange}
                      handlePageSizeChange={handlePageSizeChange}
                    />
                  )}
                </Loading>
              )
            })(),
          },
          ...versionTabs,
          ...economicProfileVersionTabs,
        ]}
      />
    </React.Fragment>
  )
}

export default AppropriatenessTestPage
